import {
  Component,
  EventEmitter,
  Input,
  Output,
  NgZone,
  ChangeDetectorRef,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import {
  PopularDateRangesService,
  DateRangeId,
} from '../../core/_services/popular-date-ranges.service';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
})
export class DateRangePickerComponent {
  // Backing fields for date values
  private _lowerRange: Date | null = null;
  private _upperRange: Date | null = null;

  // Accept either a Date or a string; if string, convert it to a local Date.
  @Input() set lowerRange(value: Date | string | null) {
    if (typeof value === 'string') {
      this._lowerRange = this.parseLocalDate(value);
    } else {
      this._lowerRange = value;
    }
  }
  get lowerRange(): Date | null {
    return this._lowerRange;
  }

  @Input() set upperRange(value: Date | string | null) {
    if (typeof value === 'string') {
      this._upperRange = this.parseLocalDate(value);
    } else {
      this._upperRange = value;
    }
  }
  get upperRange(): Date | null {
    return this._upperRange;
  }

  @Input() labelWidth = '125';
  @Input() showNextOptions = true;
  @Input() showPastOptions = true;
  @Input() hideQuickSelect = false;
  @Input() label = '';
  @Input() required = false;
  @Input() startDateLabelText = 'Start Date';
  @Input() endDateLabelText = 'End Date';
  @Input() disabled = false;

  // Emit formatted local date strings (yyyy-MM-dd) or null
  @Output() lowerRangeChanged = new EventEmitter<string | null>();
  @Output() upperRangeChanged = new EventEmitter<string | null>();
  @Output() change = new EventEmitter<{
    lowerRange: string | null;
    upperRange: string | null;
  }>();

  myId = uuidv4();

  constructor(
    private popularDateRanges: PopularDateRangesService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  // Parse a string in the format "yyyy-MM-dd" as a local date.
  private parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  // Format a Date using its local values as "yyyy-MM-dd"
  private formatLocalDate(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // When a quick select option (like "This Week") is chosen.
  onDateRangeSelected(id: string): void {
    this.ngZone.run(() => {
      const range = this.popularDateRanges.getDateRangeById(id as DateRangeId);
      if (range) {
        this._lowerRange = range.start;
        this._upperRange = range.end;
        this.emitChange();
      } else {
        console.warn(`No date range found for ID: ${id}`);
      }
    });
  }

  // Emit the change events with correctly formatted local date strings.
  private emitChange(): void {
    const formattedLower = this.formatLocalDate(this._lowerRange);
    const formattedUpper = this.formatLocalDate(this._upperRange);
    this.lowerRangeChanged.emit(formattedLower);
    this.upperRangeChanged.emit(formattedUpper);
    this.change.emit({
      lowerRange: formattedLower,
      upperRange: formattedUpper,
    });
  }

  onMenuClosed(): void {
    this.cdr.detectChanges();
  }

  clearDateRange(): void {
    this._lowerRange = null;
    this._upperRange = null;
    this.emitChange();
  }

  onLowerRangeChanged(): void {
    if (
      this._lowerRange &&
      this._upperRange &&
      this._lowerRange > this._upperRange
    ) {
      console.warn('Start date cannot be after end date.');
      this._lowerRange = null;
      this.emitChange();
      return;
    }
    this.emitChange();
  }

  onUpperRangeChanged(): void {
    if (
      this._lowerRange &&
      this._upperRange &&
      this._upperRange < this._lowerRange
    ) {
      console.warn('End date cannot be before start date.');
      this._upperRange = null;
      this.emitChange();
      return;
    }
    this.emitChange();
  }
}
